/**
 * 侧边导航菜单配置（4 组 15 项 · P2 菜单轻量合并 20→15）。
 * 对齐 admin 模式：数据驱动，增删叶子仅改本文件，不改 Sidebar 组件。
 *
 * 合并壳（纯壳 ?tab= 持态 + lazy 挂载既有页面组件，范本 merchant issue-center）：
 * - 价差收益   /agent/settlement/spread?tab=overview|statements|withdrawals
 * - 资质与开户 /agent/settings/profile?tab=profile|onboarding
 * - 安全与设置 /agent/settings/security?tab=security|preferences
 * - 收款账户   /agent/settings/payouts?tab=settlement|distribute（结算/分销两 Tab 分列 · PRD 硬约束）
 *
 * 固定树七节点核对：经营总览(全局数据大屏) / 数据报表 / 异常通知 / 收款账户(分列) /
 * 对账中心 / 结算配置 / 回退记录 —— 全部在列。
 * 红线：代理端禁交易明细/GMV/笔数/持卡人（RL-001）；文案统一「价差」（UI-R07）。
 */
import type { ReactNode } from 'react';
import {
  LayoutDashboard,
  Users,
  Wallet,
  ShieldAlert,
  Settings,
  Building2,
  KeyRound,
  Landmark,
  Percent,
  FileText,
  FileSignature,
  Tag,
  Calculator,
  CircleDollarSign,
  CalendarClock,
  Scale,
  RotateCcw,
} from 'lucide-react';

export interface LeafItem {
  label: string;
  path: string;
  badge?: number;
  icon?: ReactNode;
}
export interface GroupItem {
  label: string;
  icon: ReactNode;
  leaves?: LeafItem[];
  path?: string;
}

export const MENU: GroupItem[] = [
  // 总览(1)
  {
    label: '总览',
    icon: <LayoutDashboard className="w-4 h-4" />,
    leaves: [{ label: '全局数据大屏', path: '/agent/dashboard' }],
  },
  // 展业中心(4)——P2 不动
  {
    label: '展业中心',
    icon: <Users className="w-4 h-4" />,
    leaves: [
      { label: '商户管理', path: '/agent/biz/merchants', icon: <Users className="w-4 h-4" /> },
      { label: '签约见证', path: '/agent/biz/sign-witness', icon: <FileSignature className="w-4 h-4" /> },
      { label: '授权价目表', path: '/agent/biz/authorized-prices', icon: <Tag className="w-4 h-4" /> },
      { label: '费率计算器', path: '/agent/biz/rate-calculator', icon: <Calculator className="w-4 h-4" /> },
      // 第一期不做：展业物料（PAGE-AGT-BIZ-008 / FUNC-AGT-008）
    ],
  },
  // 结算中心(7)——价差收益三页合并壳；冻结表定序
  {
    label: '结算中心',
    icon: <Wallet className="w-4 h-4" />,
    leaves: [
      { label: '价差收益', path: '/agent/settlement/spread', icon: <Percent className="w-4 h-4" /> },
      { label: '资金概览', path: '/agent/settlement/funds', icon: <CircleDollarSign className="w-4 h-4" /> },
      // badge 由 Sidebar 按 fetchNoticeUnreadCount 动态挂接（setLeafBadge）
      { label: '异常与通知', path: '/agent/settlement/notices', icon: <ShieldAlert className="w-4 h-4" /> },
      { label: '数据报表', path: '/agent/settlement/reports', icon: <FileText className="w-4 h-4" /> },
      { label: '结算配置', path: '/agent/settlement/settlement-config', icon: <CalendarClock className="w-4 h-4" /> },
      { label: '对账中心', path: '/agent/settlement/reconciliation', icon: <Scale className="w-4 h-4" /> },
      { label: '回退记录', path: '/agent/settlement/reversals', icon: <RotateCcw className="w-4 h-4" /> },
    ],
  },
  // 个人中心(3)——资质与开户 / 安全与设置 / 收款账户（结算、分销分列两 Tab）
  {
    label: '个人中心',
    icon: <Settings className="w-4 h-4" />,
    leaves: [
      { label: '资质与开户', path: '/agent/settings/profile', icon: <Building2 className="w-4 h-4" /> },
      { label: '安全与设置', path: '/agent/settings/security', icon: <KeyRound className="w-4 h-4" /> },
      { label: '收款账户', path: '/agent/settings/payouts', icon: <Landmark className="w-4 h-4" /> },
    ],
  },
];

/**
 * 给指定路径叶子挂动态徽章（badge 为 0/undefined 时隐藏，语义不变）。
 * 纯函数返回新数组，不改动 MENU 常量本身。
 */
export function setLeafBadge(menu: GroupItem[], path: string, badge?: number): GroupItem[] {
  return menu.map((group) => ({
    ...group,
    leaves: group.leaves?.map((leaf) => (leaf.path === path ? { ...leaf, badge } : leaf)),
  }));
}
